import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { CalendarView } from '../../../components/calendar/CalendarView';
import { useCalendarMeetings } from '../../../components/calendar/hooks/useCalendarMeetings';
import { CalendarItem } from '../../../components/calendar/types';
import { useCalendarNavigation } from '../../../components/calendar/useCalendarNavigation';
import { meetingToCalendarItem } from '../../../components/calendar/utils/normalize';
import { THEME } from '../../../components-v2/Theme/theme';
import { MeetingDetailsFlyout } from '../../../components/dataBoards/meetings/MeetingDetailsFlyout';
import { Drawer } from '../../../components-v2/Drawer';

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		flex: 1,
		minHeight: 0,
		gap: THEME.spacing.md,
		padding: `${THEME.spacing.md}px 0`,
	},
});

interface IProps {
	/** Board filter criteria (host / status / search) with no date bounds — the calendar owns the date window. */
	filterCriteria: Api.IScheduledMeetingFilterCriteria[];
	impersonationContext?: Api.IImpersonationContext;
	forUserId?: string;
	canUpdateStatus: boolean;
	onMeetingUpdated: () => void;
}

/**
 * Calendar view for the Meetings data board: the shared {@link CalendarView} surface driven by the
 * board's filters, meetings-only (no events).
 */
export const MeetingsCalendarView: React.FC<IProps> = ({
	filterCriteria,
	impersonationContext,
	forUserId,
	canUpdateStatus,
	onMeetingUpdated,
}) => {
	const nav = useCalendarNavigation();
	const { meetings, isLoading } = useCalendarMeetings({
		range: nav.range,
		extraCriteria: filterCriteria,
		forUserId,
		impersonationContext,
	});

	const items = React.useMemo<CalendarItem[]>(
		() => meetings.map(meetingToCalendarItem).filter((item): item is CalendarItem => !!item),
		[meetings]
	);

	// Store only the selected meeting's id and re-derive from the live list so the flyout reflects
	// the latest data (e.g. after a participant-status mutation refetches) rather than a stale snapshot.
	const [meetingIdToView, setMeetingIdToView] = React.useState<string | null>(null);
	const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
	const meetingToView = React.useMemo(
		() => (meetingIdToView ? (meetings.find(m => m.id === meetingIdToView) ?? null) : null),
		[meetingIdToView, meetings]
	);
	// Reset the open flag if the selected meeting leaves the live list, so no stale open state lingers.
	React.useEffect(() => {
		if (!meetingToView) {
			setIsDetailsOpen(false);
		}
	}, [meetingToView]);
	const onSelectItem = React.useCallback((item: CalendarItem) => {
		if (item.meeting?.id) {
			setMeetingIdToView(item.meeting.id);
			setIsDetailsOpen(true);
		}
	}, []);

	return (
		<div className={css(styleSheet.container)}>
			<CalendarView nav={nav} items={items} isLoading={isLoading} onSelectItem={onSelectItem} />

			<Drawer open={isDetailsOpen} onOpenChange={setIsDetailsOpen} swipeDirection='right'>
				{meetingToView ? (
					<MeetingDetailsFlyout
						meeting={meetingToView}
						canUpdateStatus={canUpdateStatus}
						forUserId={forUserId}
						onParticipantStatusUpdated={onMeetingUpdated}
					/>
				) : null}
			</Drawer>
		</div>
	);
};
